import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Eyebrow, Panel, Section, StateView, useAuth } from "@ozpanel/ui";
import { StatusPill } from "@ozpanel/kit";

/** A module's notification catalogue — the SOURCE OF TRUTH the platform-wide Notifications
 *  screen aggregates, not a copy of it.
 *
 *  Three parts, deliberately distinguished:
 *   - inherited: emitted by the platform watcher for EVERY module. Cannot be removed, which is
 *     why "this module has no notifications" is never a correct answer.
 *   - declared: this module's own events, from the module itself.
 *   - alerts: measurements with a level — the only part an operator can tune, done here rather
 *     than only on a central page, because the level belongs with the thing it governs. */
interface EventSpec { key: string; label: string; priority: string; because: string }
interface Payload {
  // Declared nullable on purpose. Go marshals a nil slice as null, and this tab threw
  // "cannot read properties of null" on 36 of 49 modules because the server sent alerts: null
  // and the read was d.alerts.length. The server is fixed; this stays nullable so the console
  // cannot be taken down again by a shape it did not choose.
  inherited: EventSpec[] | null;
  declared: EventSpec[] | null;
  alerts: ({ key: string; label: string; unit?: string; because: string; threshold?: number;
    deviation_pct?: number; interval_seconds?: number; sustain_samples?: number; overridden?: boolean;
    declared_threshold?: number; silenced?: boolean; value?: number; state?: string })[] | null;
}

const tone = (p: string) =>
  p === "urgent" ? "danger" : p === "high" ? "warn" : p === "low" ? "muted" : "accent";

function EventRow({ e }: { e: EventSpec }) {
  return (
    <div className="border-b border-line/60 px-4 py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13.5px] font-medium text-text">{e.label}</span>
        <code className="rounded bg-surface2 px-1.5 py-0.5 font-mono text-[11px] text-faint">{e.key}</code>
        <StatusPill tone={tone(e.priority) as never}>{e.priority}</StatusPill>
      </div>
      <p className="mt-1 text-[12.5px] leading-relaxed text-muted">{e.because}</p>
    </div>
  );
}

export function NotificationsTab({ name }: { name: string }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["module-notifications", name],
    queryFn: () => client.apiFetch<Payload>(`/ozpanel/modules/${name}/notifications`),
  });

  if (q.isLoading || q.error || !q.data)
    return <StateView loading={q.isLoading} error={q.error} errorContext="notifications" />;
  // Normalised ONCE, at the boundary, rather than a ?? at each of the eight read sites — the
  // next read added would have been the one without it.
  const d = {
    ...q.data,
    alerts: q.data.alerts ?? [],
    declared: q.data.declared ?? [],
    inherited: q.data.inherited ?? [],
  };

  return (
    <div className="space-y-4">
      <Section title="Measured — you set the level" id="mod.notif.alerts" collapsible
        hint={d.alerts.length ? `${d.alerts.length} watched` : "none declared"} bodyClassName="p-0">
        {d.alerts.length === 0 ? (
          <p className="p-4 text-[12.5px] leading-relaxed text-muted">
            This module declares no measurement of its own. It is still watched: the inherited
            events below apply to every module.
          </p>
        ) : (
          d.alerts.map((a) => (
            <div key={a.key} className="border-b border-line/60 px-4 py-3 last:border-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13.5px] font-medium text-text">{a.label}</span>
                <code className="rounded bg-surface2 px-1.5 py-0.5 font-mono text-[11px] text-faint">{a.key}</code>
                {a.silenced ? <StatusPill tone="warn">silenced</StatusPill>
                  : <StatusPill tone="accent">
                      {a.threshold ? `≥ ${a.threshold}${a.unit ? " " + a.unit : ""}` : `${a.deviation_pct}% over average`}
                      {(a.sustain_samples ?? 1) > 1 ? ` for ${a.sustain_samples} samples in a row` : ""}
                    </StatusPill>}
                {a.overridden && (
                  <span className="text-[11.5px] text-warn">
                    set by you — this module declares {a.declared_threshold ?? "—"}
                  </span>
                )}
                <span className="ml-auto font-mono text-[12px] tabular text-muted">
                  now {a.value ?? "—"} · every {a.interval_seconds}s
                </span>
              </div>
              <p className="mt-1 text-[12.5px] leading-relaxed text-muted">{a.because}</p>
            </div>
          ))
        )}
        <p className="border-t border-line/60 px-4 py-2.5 text-[12px] text-faint">
          Levels are changed on Notifications → Alert levels, which lists these same
          declarations for every module.
        </p>
      </Section>

      <Section title="Events this module emits" id="mod.notif.declared" collapsible
        hint={`${d.declared.length} declared`} bodyClassName="p-0">
        {d.declared.length === 0 ? (
          <p className="p-4 text-[12.5px] leading-relaxed text-muted">
            None of its own — which means only that it adds nothing beyond the standard set, not
            that it is unmonitored.
          </p>
        ) : d.declared.map((e) => <EventRow key={e.key} e={e} />)}
      </Section>

      <Section title="Inherited by every module" id="mod.notif.inherited" collapsible
        hint={`${d.inherited.length} standard`} bodyClassName="p-0">
        <p className="border-b border-line/60 px-4 py-3 text-[12.5px] leading-relaxed text-muted">
          Emitted by the platform watcher for every module, from its health and its systemd
          unit. They are not declared by this module and cannot be switched off per module.
        </p>
        {d.inherited.map((e) => <EventRow key={e.key} e={e} />)}
      </Section>
    </div>
  );
}
